<template>
  <v-card>
    <v-toolbar dark color="primary">
      <v-toolbar-title>User Program for {{ fullname }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon dark @click="close()">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card-text v-if="!loading">
      <v-row align="center" justify="center">
        <v-col cols="11">
          <h1 class="ma-3">Current Program</h1>
          <hr class="mb-2" />
          <v-simple-table>
            <tbody>
              <tr>
                <td><strong>Program Start Date:</strong></td>
                <td class="pl-13">
                  {{ formatDate(active_program.programStartDate) }}
                </td>
              </tr>
              <tr v-if="!editing.current_program">
                <td><strong>Current Program Block:</strong></td>
                <td :class="[!onboarding ? 'pl-13' : '']">
                  <v-btn v-if="onboarding" icon @click="editing.current_program = true"
                    ><v-icon color="primary">edit</v-icon></v-btn
                  >
                  {{ active_program.program.name }}
                </td>
              </tr>
              <tr v-else>
                <td><strong>Current Program Block:</strong></td>
                <td>
                  <user-program-form
                    mode="update"
                    class="edit-form"
                    :programId="active_program.id"
                    :currentProgramId="active_program.program.id"
                    :currentProgramName="active_program.program.name"
                    @close="
                      editing.current_program = false;
                      reloadProgram();
                    "
                  ></user-program-form>
                </td>
              </tr>
              <tr v-if="!editing.nutrition_plan">
                <td><strong>Nutrition Plan:</strong></td>
                <td>
                  <v-btn icon @click="editing.nutrition_plan = true"><v-icon color="primary">edit</v-icon></v-btn>
                  <a
                    v-if="userProfile.nutritionPlan"
                    :href="userProfile.nutritionPlan.pdf"
                    class="secondary--text"
                    target="_blank"
                    >{{ userProfile.nutritionPlan.name }}
                  </a>
                </td>
              </tr>
              <tr v-else>
                <td><strong>Nutrition Plan:</strong></td>
                <td>
                  <div class="edit-form">
                    <v-btn icon @click="editing.nutrition_plan = false"
                      ><v-icon color="secondary">cancel</v-icon></v-btn
                    >
                    <v-col>
                      <v-label v-if="userProfile.nutritionPlan"
                        >Changing from {{ userProfile.nutritionPlan.name }} to:</v-label
                      >
                      <v-row>
                        <validation-observer ref="observer" v-slot="{ invalid }" class="full-width">
                          <div class="inline">
                            <v-form>
                              <validation-provider v-slot="{ errors }" name="Type" rules="required">
                                <v-select
                                  v-model="nutrition_plan"
                                  :items="nutrition_options"
                                  label="Plan"
                                  :error-messages="errors"
                                  class="shorter-select"
                                ></v-select>
                              </validation-provider>
                            </v-form>
                            <v-btn class="ml-auto" @click="changeNutritionPlan" :disabled="invalid" :loading="saving"
                              >Apply Change</v-btn
                            >
                          </div>
                        </validation-observer>
                      </v-row>
                    </v-col>
                  </div>
                </td>
              </tr>
              <tr v-if="!editing.next_program">
                <td><strong>Next Program Block:</strong></td>
                <td>
                  <v-btn icon @click="editing.next_program = true"><v-icon color="primary">edit</v-icon></v-btn
                  >{{ nextProgramBlock.name }}
                </td>
              </tr>
              <tr v-else>
                <td><strong>Next Program Block:</strong></td>
                <td>
                  <user-program-form
                    mode="next"
                    :programId="active_program.id"
                    :currentProgramId="nextProgramBlock.id"
                    :currentProgramName="nextProgramBlock.name"
                    @close="
                      editing.next_program = false;
                      reloadProgram();
                    "
                  ></user-program-form>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <h1 class="ma-3 mt-10">Previous Programs</h1>
          <hr class="mb-2" />
          <v-simple-table>
            <thead>
              <tr>
                <th class="text-left">Program Start Date</th>
                <th class="text-left">Coach</th>
                <th class="text-left">Program Name</th>
                <th class="text-left">Nutrition Plan</th>
                <th class="text-left">Check-in Date:</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="program in previous_programs" :key="program.id">
                <td>
                  {{ formatDate(program.programStartDate) }}
                </td>
                <td>
                  <span v-if="program.coach"> {{ program.coach.firstName }} {{ program.coach.lastName }} </span>
                </td>
                <td>
                  {{ program.program.name }}
                </td>
                <td>
                  {{ program.nutritionPlan && program.nutritionPlan.name }}
                </td>
                <td>
                  <div v-if="program.assessments.length == 0">
                    <v-btn small @click="showAssessmentModal">Create Assessment</v-btn>
                    <v-dialog v-model="modals.assessmentModal" width="900">
                      <new-assessment :close="closeAssessmentModal" :userId="userId"></new-assessment>
                    </v-dialog>
                  </div>
                  <div v-else>
                    {{ program.assessments[0].requiredDate | moment("YYYY-MM-DD hh:mm") }}
                  </div>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { USER_PROGRAMS_QUERY } from "../../../constants/graphql/user_program/user_program";
import { NUTRITION_PLANS_QUERY, UPDATE_NUTRITION_PLAN_MUTATION } from "../../../constants/graphql/nutrition/nutrition";
import { YYYYMMDD_readable } from "../../../lib/fancy_date";
import UserProgramForm from "./UserProgramForm";
import NewAssessment from "../../dashboard/components/NewAssessment";

export default {
  name: "UserProgram",
  props: {
    close: {
      type: Function,
      default: null,
    },
    entry: {
      type: Object,
      default: null,
    },
    userId: {
      type: String,
      default: null,
    },
    fullname: {
      type: String,
      default: null,
    },
  },
  components: {
    UserProgramForm,
    NewAssessment,
  },
  data() {
    return {
      loading: true,
      saving: false,
      userProfile: null,
      editing: {
        current_program: false,
        next_program: false,
        nutrition_plan: false,
      },
      modals: {
        assessmentModal: false,
      },
      active_program: {},
      previous_programs: [],
      nutrition_plan: null,
      nutrition_options: [],
    };
  },
  computed: {
    nextProgramBlock() {
      return this.active_program.nextProgram
        ? this.active_program.nextProgram
        : this.active_program.program.nextProgramBlock
        ? this.active_program.program.nextProgramBlock
        : "";
    },
    onboarding() {
      return this.active_program.program.name !== "Onboarding";
    },
  },
  methods: {
    formatDate(date) {
      return YYYYMMDD_readable(date);
    },
    closeAssessmentModal() {
      this.modals.assessmentModal = false;
    },
    showAssessmentModal() {
      this.modals.assessmentModal = true;
    },
    changeNutritionPlan() {
      this.saving = true;
      let nutrition_plan_id = this.nutrition_options.find((val) => val.text == this.nutrition_plan).id;

      this.$apollo
        .mutate({
          mutation: UPDATE_NUTRITION_PLAN_MUTATION,
          variables: {
            userId: this.userId,
            nutritionPlanId: nutrition_plan_id,
          },
        })
        .then((result) => {
          if (!result.data.updateNutritionPlan.updated) {
            alert(result.data.updateNutritionPlan.error);
          } else {
            this.$toasted.success("Nutrition Plan updated", {
              icon: "arm-flex",
              duration: 2000,
              position: "bottom-center",
            });
          }
          this.saving = false;
          this.editing.nutrition_plan = false;
          this.$refs.observer.reset();
          this.reloadProgram();
        })
        .catch((error) => {
          this.saving = false;
        });
    },
    reloadProgram() {
      this.$apollo.queries.userPrograms.refetch();
    },
  },
  apollo: {
    userPrograms: {
      query: USER_PROGRAMS_QUERY,
      variables() {
        return {
          userId: this.userId,
        };
      },
      result(response) {
        this.userProfile = response.data.clientUserPrograms.userProfile;
        this.active_program = response.data.clientUserPrograms.userPrograms.find(
          (program) => program.disabled === false
        );
        this.previous_programs = response.data.clientUserPrograms.userPrograms.filter(
          (program) => program.id !== this.active_program.id
        );
      },
      update: (data) => data.clientUserPrograms.userPrograms,
    },
    nutritionPlans: {
      query: NUTRITION_PLANS_QUERY,
      result(response) {
        this.nutrition_options = response.data.nutritionPlans.map((val) => {
          let name = val.name.replace("Nutrition Plan", "");
          return { id: val.id, text: name };
        });
        this.nutrition_options.sort((a, b) => (a.text > b.text ? 1 : -1));
        this.loading = false;
      },
    },
  },
};
</script>

<style scoped>
.shorter-select {
  max-width: 200px;
  margin-left: 10px;
}

table tr > td:first-of-type {
  width: 25%;
}

.edit-form {
  display: inline-flex;
  position: relative;
  width: 100%;
}
.full-width {
  width: 100%;
}
.inline {
  display: inline-flex;
  align-items: center;
  width: 100%;
}
.inline > button {
  align-self: center;
}
</style>
